import React, { ChangeEvent, useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import _debounce from 'lodash/debounce';
import { Button, Col, Dropdown, DropdownButton, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BsSortDown, BsSortUp } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { CampaignCategory } from '../../../../../model/campaign/CampaignCategory';
import { CustomAutocomplete } from '../../../../../shared/components/Autocomplete';
import { capitalizeFirstLetter, truncateString } from '../../../../../shared/functions/Functions';
import { IAllStates } from '../../../../../store/rootReducer';
import { BookingService } from '../service';
import { IInviteFilterProps } from './dto/IInviteFilterProps';
import { IPastCampaign } from './dto/IPastCampaig';
import { useSocialPlatform } from '../../../../../shared/hooks/useSocialPlatform';

export function InviteFilter({ info, filterParams, setFilterParams, userCount, isFilterShown }: IInviteFilterProps) {
    const { t } = useTranslation();

    const [category, setCategory] = useState<CampaignCategory[]>([]);
    const [dropdownCategories, setDropdownCategories] = useState<CampaignCategory[]>([]);
    const { gender, country } = useSelector((states: IAllStates) => states.enums);
    const [pastCampaigns, setPastCampaigns] = useState<IPastCampaign[]>([]);
    const [filterParamsCampaign, setFilterParamsCampaign] = useState({ title: '', page: 1 });
    const { platforms: socialPlatforms } = useSocialPlatform({ isEnabled: true });
    const isFilterDisabled = !!filterParams?.influencerName?.length;
    const audienceCountries = filterParams?.audienceLocation?.map(item => t(`general.countries.${item}`));
    const influencerCountries = filterParams?.influencerLocation?.map(item => t(`general.countries.${item}`));
    const filteredCountries = (audienceCountries ?? [])?.concat(influencerCountries ?? []);

    useEffect(() => {
        BookingService.getPastCampaign(filterParamsCampaign).then(response => {
            setPastCampaigns(response?.data);
        });
    }, [JSON.stringify(filterParamsCampaign)]);

    useEffect(() => {
        BookingService.getCategory().then(response => {
            setCategory(response);
            // we need this to remove _ in category names
            setDropdownCategories(
                response?.map(cat => ({
                    ...cat,
                    name: capitalizeFirstLetter(cat?.name?.split('_')?.join(' ')),
                }))
            );
        });
    }, []);

    const changeHandler = _debounce((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event?.target?.value;
        setFilterParams({
            ...filterParams,
            [event.target.name]: value,
            page: 1,
        });
    }, 300);

    const multipleSelectHandler = (paramKey: string, paramValue: any) => {
        setFilterParams({
            ...filterParams,
            [paramKey]: !!`${paramValue}`?.length ? paramValue : null,
            page: 1,
        });
    };

    const platforms = socialPlatforms.filter(item => info?.platformId.find(id => item.id === id));

    const interests = category.filter((item: any) => info?.categoryId.find(id => item.id === id));

    const changeParamsHandler = (paramKey: string, paramValue: string | string[] | number) => {
        setFilterParams({ ...filterParams, [paramKey]: paramValue !== 'null' ? (paramValue as number) : 0 });
    };

    return (
        <>
            {!!isFilterShown && (
                <>
                    <Col sm={6} md={4} className="mb-3">
                        <CustomAutocomplete
                            dataArray={pastCampaigns}
                            changeEventKey="title"
                            isWithImage={false}
                            inputLabel="title"
                            photoLabel=""
                            isDisabled={isFilterDisabled}
                            queryFilterKey="pastCampaignId"
                            setFilterState={setFilterParamsCampaign}
                            inputLabelTranslation={`${t('brand.campaign.book.invite.filters.past_campaigns')}`}
                            handleChangeParams={changeParamsHandler}
                            isMultiple={false}
                        />
                    </Col>
                    <Col sm={6} md={4} className="mb-3">
                        {info && (
                            <CustomAutocomplete
                                dataArray={socialPlatforms}
                                defaultArray={(socialPlatforms as any)?.filter((item: any) =>
                                    info?.platformId.find(id => item.id === id)
                                )}
                                isWithImage={true}
                                inputLabel="name"
                                queryFilterKey="platformId"
                                photoLabel="path"
                                isMultiple={true}
                                isDisabled={isFilterDisabled}
                                inputLabelTranslation={`${t('brand.campaign.book.invite.filters.platforms')}`}
                                handleChangeParams={multipleSelectHandler}
                            />
                        )}
                    </Col>
                    <Col sm={6} md={4} className="mb-3">
                        <DropdownButton
                            disabled={isFilterDisabled}
                            className={`dropdown-secondary location-dropdown ${
                                filterParams?.reachFrom ? 'active' : ''
                            }`}
                            title={`${t('brand.campaign.book.invite.filters.total_reach')}: ${
                                filterParams?.reachFrom ?? ''
                            } ${filterParams?.reachTo && filterParams?.reachTo ? '-' : ''} ${
                                filterParams?.reachTo ?? ''
                            }`}
                        >
                            <Row>
                                <Col lg={6}>
                                    <TextField
                                        label={t('brand.campaign.book.offer.filters.reach_from')}
                                        variant="outlined"
                                        size="small"
                                        disabled={isFilterDisabled}
                                        name="reachFrom"
                                        type="number"
                                        onChange={changeHandler}
                                        className="w-100 mb-4 my-lg-0"
                                        InputProps={{ inputProps: { min: 0 } }}
                                        onKeyPress={event =>
                                            (event?.key === '-' || event?.key === '+') && event.preventDefault()
                                        }
                                    />
                                </Col>
                                <Col lg={6}>
                                    <TextField
                                        label={t('brand.campaign.book.offer.filters.reach_to')}
                                        variant="outlined"
                                        onChange={changeHandler}
                                        size="small"
                                        type="number"
                                        name="reachTo"
                                        className="w-100"
                                        InputProps={{ inputProps: { min: filterParams?.reachFrom } }}
                                        onKeyPress={event =>
                                            (event?.key === '-' || event?.key === '+') && event.preventDefault()
                                        }
                                    />
                                </Col>
                            </Row>
                        </DropdownButton>
                    </Col>
                    <Col sm={6} md={4} className="mb-3">
                        <DropdownButton
                            className={`dropdown-secondary location-dropdown w-100 ${
                                !!filteredCountries?.length ? 'active' : ''
                            }`}
                            title={`${t('brand.campaign.book.offer.filters.location')} ${truncateString(
                                filteredCountries
                                    ?.map(item => item)
                                    .slice(0, 3)
                                    ?.join(', '),
                                15
                            )} ${
                                (filteredCountries?.length ?? 0) > 3 ? `+ ${(filteredCountries?.length ?? 0) - 3}` : ''
                            }`}
                            variant="transparent"
                            disabled={isFilterDisabled}
                        >
                            <Row>
                                <Col lg={6}>
                                    <h6 className="mb-3">{t('brand.campaign.book.offer.filters.audience_location')}</h6>

                                    <CustomAutocomplete
                                        dataArray={country}
                                        isWithImage={true}
                                        inputLabel="country"
                                        isMultiple={true}
                                        defaultArray={(info as any)?.audienceLocation}
                                        photoLabel="countryFlag"
                                        isDisabled={isFilterDisabled}
                                        queryFilterKey="audienceLocation"
                                        inputLabelTranslation={`${t('brand.campaign.book.offer.filters.location')}`}
                                        handleChangeParams={multipleSelectHandler}
                                    />
                                </Col>
                                <Col lg={6}>
                                    <h6 className="mt-3 mt-lg-0 mb-3">{`${t(
                                        'brand.campaign.book.offer.filters.influencer_location'
                                    )}`}</h6>

                                    <CustomAutocomplete
                                        dataArray={country}
                                        isWithImage={true}
                                        inputLabel="country"
                                        isMultiple={true}
                                        defaultArray={(info as any)?.influencerLocation}
                                        photoLabel="countryFlag"
                                        isDisabled={isFilterDisabled}
                                        queryFilterKey="influencerLocation"
                                        inputLabelTranslation={`${t('brand.campaign.book.offer.filters.location')}`}
                                        handleChangeParams={multipleSelectHandler}
                                    />
                                </Col>
                            </Row>
                        </DropdownButton>
                    </Col>
                    <Col sm={6} md={4} className="mb-3">
                        {info && (
                            <CustomAutocomplete
                                dataArray={dropdownCategories}
                                defaultArray={(dropdownCategories as any).filter((item: any) =>
                                    info?.categoryId.find(id => item.id === id)
                                )}
                                isMultiple={true}
                                isWithImage={false}
                                inputLabel="name"
                                queryFilterKey="categoryId"
                                isDisabled={isFilterDisabled}
                                inputLabelTranslation={t('influencer.profile.categories')}
                                handleChangeParams={multipleSelectHandler}
                            />
                        )}
                    </Col>
                    <Col sm={6} md={4} className="mb-3">
                        {info && (
                            <CustomAutocomplete
                                dataArray={gender}
                                defaultArray={info?.gender}
                                isMultiple={false}
                                isWithImage={false}
                                inputLabel="gender"
                                queryFilterKey="gender"
                                isDisabled={isFilterDisabled}
                                inputLabelTranslation={`${t('brand.campaign.book.invite.filters.gender')}`}
                                handleChangeParams={changeParamsHandler}
                            />
                        )}
                    </Col>
                    <Col sm={6} lg={6} xl={4} className="d-none d-sm-flex">
                        <TextField
                            label={`${t('brand.campaign.book.invite.filters.influencer_city')}`}
                            variant="outlined"
                            size="small"
                            name="influencerCity"
                            className="w-100"
                            onChange={changeHandler}
                        />
                    </Col>
                    {/*<Col sm={6} lg={6} xl={4} className="d-none d-sm-flex">*/}
                    {/*    <TextField*/}
                    {/*        label={`${t('brand.campaign.book.invite.filters.audience_city')}`}*/}
                    {/*        variant="outlined"*/}
                    {/*        size="small"*/}
                    {/*        name="audienceCity"*/}
                    {/*        className="w-100"*/}
                    {/*        onChange={changeHandler}*/}
                    {/*    />*/}
                    {/*</Col>*/}
                </>
            )}
            <div className="d-flex flex-wrap my-2 justify-content-between w-100">
                <Col lg={8}>
                    <p>
                        {t('brand.campaign.book.components.filterText.total')} <strong>{userCount}</strong>{' '}
                        {t('brand.campaign.book.components.filterText.influencersFound')}{' '}
                        {t('brand.campaign.book.components.filterText.from')}{' '}
                        <strong>
                            {(info?.influencerLocation as any)
                                ?.map((location: string) => t(`general.countries.${location}`))
                                .join(', ')}{' '}
                        </strong>
                        {t('brand.campaign.book.components.filterText.withInterestsIn')}{' '}
                        <strong>{interests.map(item => t(`general.category.${item.name}`)).join(', ')}</strong>{' '}
                        {t('brand.campaign.book.components.filterText.onSocialMediaPlatforms')}:{' '}
                        {platforms.map((item, index) => (
                            <strong key={item.name}>
                                {item.name}
                                {index === platforms.length - 1 ? '' : ', '}
                            </strong>
                        ))}
                    </p>
                </Col>
                <Col lg={4} className="col">
                    <div className="d-flex align-items-center justify-content-end">
                        <Button
                            onClick={() =>
                                setFilterParams({
                                    ...filterParams,
                                    sortType: filterParams.sortType === 'DESC' ? 'ASC' : 'DESC',
                                })
                            }
                            variant="transparent"
                            aria-label="Sort type"
                        >
                            {filterParams.sortType === 'ASC' ? (
                                <BsSortUp style={{ height: 16, width: 16 }} />
                            ) : (
                                <BsSortDown style={{ height: 16, width: 16 }} />
                            )}
                        </Button>
                        <DropdownButton
                            className="font-weight-normal pe-0"
                            variant="transparent"
                            title={
                                <span>
                                    {t(
                                        !filterParams?.sortCriteria
                                            ? 'general.button.sort_by'
                                            : `brand.campaign.book.offer.filters.${filterParams?.sortCriteria}`
                                    )}
                                </span>
                            }
                        >
                            {filterParams?.sortCriteria && (
                                <Dropdown.Item
                                    onClick={() =>
                                        setFilterParams({
                                            ...filterParams,
                                            sortCriteria: '',
                                        })
                                    }
                                >
                                    Clear
                                </Dropdown.Item>
                            )}
                            <Dropdown.Item
                                onClick={() =>
                                    setFilterParams({
                                        ...filterParams,
                                        sortCriteria: 'totalFollowers',
                                    })
                                }
                            >
                                {t('brand.campaign.book.offer.filters.totalFollowers')}
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() =>
                                    setFilterParams({
                                        ...filterParams,
                                        sortCriteria: 'mostActive',
                                    })
                                }
                            >
                                {t('brand.campaign.book.offer.filters.mostActive')}
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() =>
                                    setFilterParams({
                                        ...filterParams,
                                        sortCriteria: 'totalEngagements',
                                    })
                                }
                            >
                                {t('brand.campaign.book.offer.filters.totalEngagements')}
                            </Dropdown.Item>
                        </DropdownButton>
                    </div>
                </Col>
            </div>
        </>
    );
}
